import { OnboardingInput } from "@/features/onboarding/components/onboarding-input";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { OnboardingUnitToggle } from "@/features/onboarding/components/onboarding-unit-toggle";
import { triggerHaptic } from "@/utils/haptics";
import { ReactNode, useState } from "react";

type WeightUnit = "kg" | "lbs";

const UNIT_OPTIONS = [
  { value: "kg", label: "kg" },
  { value: "lbs", label: "lbs" },
] as const;

const KG_PER_LB = 0.45359237;
const MIN_KG = 25;
const MAX_KG = 350;

const round1 = (n: number) => Math.round(n * 10) / 10;

const toKg = (value: number, unit: WeightUnit) => (unit === "kg" ? value : value * KG_PER_LB);
const fromKg = (kg: number, unit: WeightUnit) => (unit === "kg" ? kg : kg / KG_PER_LB);

interface OnboardingWeightStepProps {
  title: ReactNode;
  subtitle: string;
  isSubmitting: boolean;
  /** Receives the entered weight converted to kilograms (1-decimal). */
  onSubmit: (kg: number) => void;
}

/** Shared weight-entry layout for the "Starting From" / "Getting To" v5 screens. */
export const OnboardingWeightStep = ({
  title,
  subtitle,
  isSubmitting,
  onSubmit,
}: OnboardingWeightStepProps) => {
  const [unit, setUnit] = useState<WeightUnit>("kg");
  const [input, setInput] = useState("");

  const parsed = Number(input.replace(",", "."));
  const kg = input && !isNaN(parsed) ? round1(toKg(parsed, unit)) : null;
  const isValid = kg !== null && kg >= MIN_KG && kg <= MAX_KG;

  const handleUnitChange = (next: WeightUnit) => {
    if (next === unit) return;
    if (input && !isNaN(parsed)) {
      setInput(String(round1(fromKg(toKg(parsed, unit), next))));
    }
    setUnit(next);
  };

  const handleContinue = () => {
    if (!isValid || isSubmitting || kg === null) return;
    triggerHaptic();
    onSubmit(kg);
  };

  return (
    <OnboardingStepShellV5
      title={title}
      subtitle={subtitle}
      footer={
        <OnboardingPillButton
          text="Continue"
          onClick={handleContinue}
          disabled={!isValid || isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <OnboardingUnitToggle options={UNIT_OPTIONS} value={unit} onChange={handleUnitChange} />
        <OnboardingInput
          type="text"
          inputMode="decimal"
          placeholder="Add weight (e.g. 52.5)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
      </div>
    </OnboardingStepShellV5>
  );
};
